import { create } from "zustand";

type ContentRating = "safe" | "suggestive" | "nsfw";

// Persisted flag: the user has confirmed they are 18+ on this device.
// Only asked once; the SFW/NSFW toggle itself stays session-only.
const AGE_CONFIRMED_KEY = "studiocomm-age-confirmed";

function readAgeConfirmed(): boolean {
  try {
    return localStorage.getItem(AGE_CONFIRMED_KEY) === "true";
  } catch {
    return false;
  }
}

function writeAgeConfirmed(): void {
  try {
    localStorage.setItem(AGE_CONFIRMED_KEY, "true");
  } catch {
    // Private mode / storage disabled — confirmation lasts for the session only.
  }
}

interface UIState {
  // Content rating filter - array of selected ratings
  // Default: ["safe"] - only safe content
  // Multiple can be selected to show multiple ratings
  selectedContentRatings: ContentRating[];
  isMenuOpen: boolean;

  // Age gate: true once the user has confirmed they are 18+ on this device.
  hasConfirmedAdult: boolean;
  // Rating waiting for age confirmation; non-null opens the AgeGateModal.
  pendingAdultRating: ContentRating | null;
  confirmAdult: () => void;
  cancelAdultConfirmation: () => void;

  // Toggle a single rating on/off
  toggleContentRating: (rating: ContentRating) => void;
  // Set specific ratings (for programmatic control)
  setContentRatings: (ratings: ContentRating[]) => void;
  // Legacy single-select support (used by API calls)
  getContentRatingParam: () => string;

  toggleMenu: () => void;
  closeMenu: () => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  selectedContentRatings: ["safe"], // Default to safe only
  isMenuOpen: false,

  hasConfirmedAdult: readAgeConfirmed(),
  pendingAdultRating: null,

  confirmAdult: () => {
    writeAgeConfirmed();
    set((state) => {
      const pending = state.pendingAdultRating;
      const shouldAdd = pending && !state.selectedContentRatings.includes(pending);
      return {
        hasConfirmedAdult: true,
        pendingAdultRating: null,
        selectedContentRatings: shouldAdd
          ? [...state.selectedContentRatings, pending]
          : state.selectedContentRatings,
      };
    });
  },

  cancelAdultConfirmation: () => set({ pendingAdultRating: null }),

  toggleContentRating: (rating) => set((state) => {
    const current = state.selectedContentRatings;
    const isSelected = current.includes(rating);

    // Enabling NSFW for the first time requires age confirmation.
    if (rating === "nsfw" && !isSelected && !state.hasConfirmedAdult) {
      return { pendingAdultRating: rating };
    }

    if (isSelected && current.length === 1) {
      // Can't deselect the last one - at least one must remain
      return state;
    } else if (isSelected) {
      // Multi-select: remove from selection
      return {
        selectedContentRatings: current.filter(r => r !== rating)
      };
    } else {
      // Multi-select: add to selection
      return {
        selectedContentRatings: [...current, rating]
      };
    }
  }),

  setContentRatings: (ratings) => set((state) => {
    if (ratings.includes("nsfw") && !state.hasConfirmedAdult) {
      const withoutNsfw = ratings.filter((r) => r !== "nsfw");
      return {
        pendingAdultRating: "nsfw",
        selectedContentRatings: withoutNsfw.length > 0 ? withoutNsfw : ["safe"],
      };
    }
    return {
      selectedContentRatings: ratings.length > 0 ? ratings : ["safe"]
    };
  }),

  // Returns comma-separated ratings for API calls
  getContentRatingParam: () => {
    return get().selectedContentRatings.join(",");
  },

  toggleMenu: () => set((state) => ({ isMenuOpen: !state.isMenuOpen })),
  closeMenu: () => set({ isMenuOpen: false }),
}));
